/**
 * ProfileSettings.tsx — T321 SETTINGS-1 / FR-004 rewrite.
 *
 * Wires the per-user profile to
 *   - api.organizations.getCurrentUserSecurity (read; identity + member info)
 *   - api.organizations.updateMemberProfile    (auth-only mutation; patches
 *                                               the current principal's row)
 *
 * Honest-state contract: Save is routed through useMutationWithToast
 * (FR-013 (H1), FR-004 (S3)). Logs argument KEYS only (never values).
 *
 * H4 ratchet: this file is no longer the thin re-export shell — the
 * standalone body satisfies the per-sub-page S2 test in
 * frontend-v3/tests/destub/honest-state.test.ts (FR-004).
 */
import { useEffect, useState } from 'react';
import { useQuery } from 'convex/react';
import {
  Avatar,
  Btn,
  Field,
  Input,
  Panel,
  Pill,
} from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { api } from '@convex/api';

interface CurrentUserSecurity {
  subject?: string | null;
  email?: string | null;
  name?: string | null;
  member?: {
    _id?: string | null;
    email?: string | null;
    name?: string | null;
    role?: string | null;
    status?: string | null;
  } | null;
}

export function ProfileSettings() {
  const current = useQuery(api.organizations.getCurrentUserSecurity) as
    | CurrentUserSecurity
    | undefined;

  const updateMemberProfileM = useMutationWithToast(
    api.organizations.updateMemberProfile,
    {
      logLabel: 'organizations_updateMemberProfile',
      successMessage: 'Perfil actualizado.',
      errorMessagePrefix: 'No se pudo actualizar el perfil',
    },
  );

  const [name, setName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');

  useEffect(() => {
    if (!current) return;
    if (typeof current.name === 'string') setName(current.name);
    else if (typeof current.member?.name === 'string') {
      setName(current.member.name);
    }
  }, [current]);

  const handleSave = () => {
    void updateMemberProfileM.run({
      name,
      avatarUrl,
    });
  };

  const isSaving = updateMemberProfileM.loading;
  const isLoading = current === undefined;
  const displayEmail = current?.email ?? current?.member?.email ?? '';

  return (
    <div className="circa-cols-8-4">
      <Panel title="Identidad" pad={28}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div
            style={{
              display: 'flex',
              gap: 16,
              alignItems: 'center',
              marginBottom: 4,
            }}
          >
            <Avatar name={name || 'Usuario'} size={64} />
            <Btn
              variant="ghost"
              size="sm"
              onClick={() => {
                // Avatar URL is editable below; the link button is a
                // visual affordance only — the real change happens via
                // the Input field and the Save button.
              }}
            >
              Cambiar avatar
            </Btn>
          </div>
          <Field label="Nombre completo">
            <Input
              value={name}
              onChange={setName}
              disabled={isLoading || isSaving}
            />
          </Field>
          <Field label="URL del avatar">
            <Input
              value={avatarUrl}
              onChange={setAvatarUrl}
              placeholder="https://…"
              disabled={isLoading || isSaving}
            />
          </Field>
          <Field
            label="Correo"
            hint="Cambia tu correo desde Ajustes de cuenta."
          >
            <Input value={displayEmail} onChange={() => {}} disabled />
          </Field>
          <div>
            <Btn
              variant="solid"
              onClick={handleSave}
              disabled={isLoading || isSaving}
            >
              {isSaving ? 'Guardando…' : 'Guardar'}
            </Btn>
          </div>
        </div>
      </Panel>
      <Panel title="Métodos vinculados">
        {(
          [
            ['Correo y contraseña', true],
            ['Google', true],
            ['GitHub', false],
          ] as Array<[string, boolean]>
        ).map(([label, linked]) => (
          <div
            key={label}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 0',
              borderBottom: '1px solid var(--lightest-gray)',
              fontSize: 14,
            }}
          >
            <span>{label}</span>
            {linked ? (
              <Pill tone="good" size="sm">
                Vinculado
              </Pill>
            ) : (
              <Btn size="sm" variant="text">
                Vincular
              </Btn>
            )}
          </div>
        ))}
      </Panel>
    </div>
  );
}

export default ProfileSettings;
